import { getUserDataLoaded, getUserFirstName, getUserFullName, getUserLastName } from './user';
import api from '@/redux/api/feedback';
import { createSelector } from 'reselect';
import { getHelpScoutInformation } from './config';
import { GlobalState } from '../rootReducer';
import LogRocketLib from 'logrocket';

/* Action Types */
const RESET_FEEDBACK_MODAL = 'la/ui/submitFeedback/RESET';
const SUBMIT_FEEDBACK_FAIL = 'la/ui/submitFeedback/FAIL';
const SUBMIT_FEEDBACK_REQUEST = 'la/ui/submitFeedback/REQUEST';
const SUBMIT_FEEDBACK_SUCCESS = 'la/ui/submitFeedback/SUCCESS';

const FETCH_HELPSCOUT_TOKEN_FAIL = 'la/ui/fetchHelpScoutToken/FAIL';
const FETCH_HELPSCOUT_TOKEN_REQUEST = 'la/ui/fetchHelpScoutToken/REQUEST';
const FETCH_HELPSCOUT_TOKEN_SUCCESS = 'la/ui/fetchHelpScoutToken/SUCCESS';

// reducer
const DEFAULT_STATE = {
    accessToken: '',
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case RESET_FEEDBACK_MODAL:
            return {
                ...state,
                submitted: false,
                success: false,
            };
        case SUBMIT_FEEDBACK_FAIL:
            return {
                ...state,
                submitted: false,
                success: false,
            };
        case SUBMIT_FEEDBACK_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case SUBMIT_FEEDBACK_SUCCESS:
            return {
                ...state,
                submitted: false,
                success: true,
            };
        case FETCH_HELPSCOUT_TOKEN_FAIL:
            return {
                ...state,
            };
        case FETCH_HELPSCOUT_TOKEN_REQUEST:
            return {
                ...state,
            };
        case FETCH_HELPSCOUT_TOKEN_SUCCESS:
            return {
                ...state,
                accessToken: action.payload,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const feedbackSelector = (state: GlobalState) => state.feedback;

export const getAccessToken = createSelector(feedbackSelector, (state) => state.accessToken);

export const getSubmitted = createSelector(feedbackSelector, (state) => state.submitted);

export const getSuccess = createSelector(feedbackSelector, (state) => state.success);

/* ACTION CREATORS */
export const resetModal = () => ({
    type: RESET_FEEDBACK_MODAL,
});

type SubmitFeedbackParams = {
    currentURL: string;
    email: string;
    feedback: string;
    isBiddingConsole: boolean;
};

type CustomerType = {
    email: string;
    firstName?: string;
    fullName?: string;
    lastName?: string;
    name?: string;
    sessionURL?: string;
};

type FeedbackObjectParams = {
    currentURL: string;
    customer: CustomerType;
    feedback: string;
    feedbackType: string;
};

const getLogRocketSessionURL = () => {
    const url = LogRocketLib.sessionURL;
    if (url.includes('https')) {
        return url;
    }
    return 'LogRocket is uninitialized';
};

const getFeedbackObject = ({ currentURL: currentUrl, customer, feedback, feedbackType }: FeedbackObjectParams) => {
    /** If the name exists then the customer data is loaded and we could have a session url */
    const text = Boolean(customer.name)
        ? `${feedback}

    LogRocket Session URL: ${getLogRocketSessionURL()}

    Current URL: ${currentUrl}`
        : `${feedback}

    Current URL: ${currentUrl}`;

    const threadData = {
        customer,
        text,
        type: 'customer',
    };

    return {
        // must be exact format for date, slice removes milliseconds.
        createdAt: new Date().toISOString().slice(0, 19) + 'Z',
        customer,
        mailboxId: 168062,
        status: 'active',
        subject: feedbackType,
        tags: [feedbackType],
        threads: [threadData],
        type: 'email',
    };
};

export const submitFeedback = ({ currentURL, email, feedback, isBiddingConsole }: SubmitFeedbackParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        // @ts-ignore
        const { access_token: authToken } = getAccessToken(state);
        const feedbackType = isBiddingConsole ? 'live bidding feedback' : 'Site Feedback';
        const userDataLoaded = getUserDataLoaded(state);

        const customer: CustomerType = { email };
        if (userDataLoaded) {
            customer.name = getUserFullName(state);
            customer.fullName = getUserFullName(state);
            customer.firstName = getUserFirstName(state);
            customer.lastName = getUserLastName(state);
            customer.sessionURL = getLogRocketSessionURL();
        }

        const feedbackObject = getFeedbackObject({ currentURL, customer, feedback, feedbackType });

        dispatch({
            payload: {
                email,
                feedback: feedbackObject,
            },
            type: SUBMIT_FEEDBACK_REQUEST,
        });
        await api.submitFeedback({
            authToken,
            feedbackObject,
        });
        dispatch({
            payload: {
                email,
                feedback: feedbackObject,
            },
            type: SUBMIT_FEEDBACK_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: SUBMIT_FEEDBACK_FAIL,
        });
    }
};

export const fetchHelpScoutToken = () => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const helpScoutInformation = getHelpScoutInformation(state);
        dispatch({
            type: FETCH_HELPSCOUT_TOKEN_REQUEST,
        });

        const response = await api.fetchHelpScoutToken(helpScoutInformation);
        dispatch({
            payload: response,
            type: FETCH_HELPSCOUT_TOKEN_SUCCESS,
        });
    } catch (error) {
        dispatch({
            type: FETCH_HELPSCOUT_TOKEN_FAIL,
        });
    }
};
